import type { ReactNode } from 'react';

import { isOrgAdmin } from '../../session/switcher/role';
import { useMe } from '../../session/session';
import type { CapacityRequest } from './api/schemas';
import { useHasOpenRequest } from './api/queries';

/** Only a tenant-wide `TENANT_ADMIN` holds `control_plane.write`; the API answers an analyst's
 *  credit request with a 403, so no entry point is offered to one. */
export function OrgAdminOnly(props: { tenantId: string; children: ReactNode }): ReactNode {
  const organisation = useMe().data?.organisations.find((o) => o.tenant_id === props.tenantId);
  return organisation !== undefined && isOrgAdmin(organisation) ? props.children : null;
}

/** Renders a request action only once the request list confirms none of its kind is open: the
 *  API refuses a second one, and offering it while the list loads would be a guess. Mounted only
 *  where the action would show, so the list is read no more often than that. */
export function NoOpenRequest(props: {
  tenantId: string;
  kind: CapacityRequest['kind'];
  children: ReactNode;
}): ReactNode {
  return (
    <OrgAdminOnly tenantId={props.tenantId}>
      <OpenRequestGate {...props} />
    </OrgAdminOnly>
  );
}

function OpenRequestGate(props: {
  tenantId: string;
  kind: CapacityRequest['kind'];
  children: ReactNode;
}): ReactNode {
  return useHasOpenRequest(props.tenantId, props.kind) === false ? props.children : null;
}
